import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { toast } from "sonner";

/**
 * AccountProfileSection
 *
 * User-level profile settings (name, email)
 * These settings follow the user across all workspaces
 */
export function AccountProfileSection() {
  const currentUser = useQuery(api.users.getCurrentUser);
  const updateProfile = useMutation(api.users.updateProfile);

  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when user data loads
  useEffect(() => {
    if (currentUser?.name) {
      setName(currentUser.name);
    }
  }, [currentUser]);

  const handleSave = async () => {
    if (!currentUser) return;
    setIsSaving(true);
    try {
      await updateProfile({ name: name.trim() });
      toast.success("Profile updated");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = currentUser
    ? name.trim() !== (currentUser.name || "")
    : false;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Profile</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Your personal information that follows you across all workspaces.
        </p>

        <div className="space-y-4 max-w-xl">
          <div className="space-y-2">
            <Label htmlFor="user-name">Name</Label>
            <div className="relative">
              <Input
                id="user-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={!currentUser}
              />
              {!currentUser && (
                <Skeleton className="absolute inset-0 rounded-md" />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="user-email">Email</Label>
            <div className="relative">
              <Input
                id="user-email"
                value={currentUser?.email || ""}
                disabled
                className="bg-muted"
              />
              {!currentUser && (
                <Skeleton className="absolute inset-0 rounded-md" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Email is managed through your authentication provider
            </p>
          </div>

          {currentUser && hasChanges && (
            <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
